import React from 'react';
import { SEOHead, getOrganizationSchema } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { Award, Globe, Users, Package, Check } from 'lucide-react';

const WAREHOUSE_IMAGE = `${process.env.PUBLIC_URL}/about_us.png`;
const TEAM_IMAGE = 'https://images.unsplash.com/photo-1758691737158-18ffa31c0a46?crop=entropy&cs=srgb&fm=jpg&w=800&q=85';

export default function AboutPage() {
  const stats = [
    { icon: Award, value: '18+', label: 'Years Experience' },
    { icon: Globe, value: 'PAN India', label: 'Delivery' },
    { icon: Users, value: '500+', label: 'Happy Partners' },
    { icon: Package, value: '4000+', label: 'Products' },
  ];

  const whyPoints = [
    'Nearly 20 Years of Industry Excellence',
    '100% Authentic & Original Perfumes',
    '500+ Brands | 4,000+ Products',
    'Authorized Middle Eastern Distribution',
    'Reliable Inventory & Long-Term Partnerships',
  ];

  const milestones = [
    { year: '2013', title: 'Foundation', description: 'Started as a small fragrance distributor in Ahmedabad' },
    { year: '2015', title: 'Expansion', description: 'Expanded our brand portfolio to 20+ international brands' },
    { year: '2018', title: 'Distribution Network', description: 'Started distribution of various middle eastern brands.' },
    { year: '2020', title: 'Digital Transformation', description: 'Launched online presence for B2B partners' },
    { year: '2023', title: 'Market Leader', description: 'Became Ahmedabad\'s largest fragrance wholesaler' },
  ];

  return (
    <>
      <SEOHead
        title="About Us - Ahmedabad's Premier Fragrance Wholesaler"
        description="Learn about Passion of Aroma - India's trusted wholesale distributor of luxury perfumes since 2013. Authorized dealer for 50+ international brands."
        canonical="https://passionofaroma.com/about"
        schemaData={getOrganizationSchema()}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0B1F3B]" data-testid="about-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'About Us' }]} />
          <div className="mt-6 max-w-3xl">
            <span className="label-text mb-4 block">Since 2007</span>
            <h1 className="heading-xl text-white mb-6">
              An Unparalleled Collection Under One Roof
            </h1>
            <p className="text-gray-300 text-lg">
              With access to over 500+ prestigious brands and 4,000+ curated fragrance offerings, we present one of the most extensive and diverse perfume collections available in the wholesale market.
            </p>
            <p className="text-gray-300 text-lg">
            From timeless designer icons to rare niche creations and opulent Middle Eastern compositions, our portfolio reflects refinement, heritage, and modern luxury.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b" data-testid="about-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 text-[#D4AF37] mx-auto mb-3" />
                <div className="text-3xl font-bold text-[#0B1F3B] mb-1 font-serif">
                  {stat.value}
                </div>
                <div className="text-gray-500 text-sm uppercase tracking-widest">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-white" data-testid="about-story">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="heading-lg mb-6">Authorized Distributor of Middle Eastern Perfume Houses</h2>
              <div className="gold-separator mb-6" />
              <div className="space-y-6 text-gray-600">
                <p>
                  We are proud to serve as a trusted distributor for leading Middle Eastern fragrance brands, offering:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Exquisite Oud &amp; Oriental masterpieces</li>
                  <li>Luxury Arabic perfumes &amp; attars</li>
                  <li>Contemporary niche creations from the Gulf</li>
                </ul>
              </div>
            </div>
            <div className="relative">
              <img
                src={WAREHOUSE_IMAGE}
                alt="Passion of Aroma store"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#0B1F3B] text-white p-6 max-w-xs">
                <p className="text-sm italic">
                  "Quality and authenticity are not just promises—they're our foundation."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serving the Fragrance Elite */}
      <section className="section-padding bg-[#F7F6F3]" data-testid="about-fragrance-elite">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="label-text mb-4 block">Our Partners</span>
            <h2 className="heading-lg mb-4">Serving the Fragrance Elite</h2>
            <div className="gold-separator mx-auto mb-6" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              Passion of Aroma partners with retailers and distributors who value consistency, discretion, and premium service.
            </p>
          </div>
            
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Luxury Perfume Boutiques', desc: 'Premium retail partners' },
              { title: 'High-End Retailers & Department Stores', desc: 'National & international retail' },
              { title: 'E-commerce & Global Marketplaces', desc: 'Online fragrance specialists' },
              { title: 'Wholesalers & Regional Distributors', desc: 'B2B distribution network' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-full bg-[#0B1F3B]/5 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#D4AF37] font-serif text-lg font-semibold">{i + 1}</span>
                </div>
                <h3 className="heading-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-gray-600 max-w-2xl mx-auto">
            Our clientele values consistency, discretion, and premium service—standards we uphold in every transaction.
            </p>
        </div>
      </section>

      {/* Why Passion of Aroma */}
      <section className="section-padding bg-[#F7F6F3]" data-testid="about-values">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="label-text mb-4 block">Why Choose Us</span>
            <h2 className="heading-lg mb-4">Why Passion of Aroma</h2>
            <div className="gold-separator mx-auto mb-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
            {whyPoints.slice(0, 3).map((point, index) => (
              <div
                key={index}
                className="bg-white rounded-lg border border-gray-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow w-full max-w-sm"
              >
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.5} />
                </div>
                <p className="text-gray-700 font-medium leading-snug">{point}</p>
              </div>
            ))}
            <div className="w-full lg:col-span-3 flex flex-col sm:flex-row flex-wrap justify-center gap-6 lg:gap-6">
              {whyPoints.slice(3, 5).map((point, index) => (
                <div
                  key={index + 3}
                  className="bg-white rounded-lg border border-gray-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow w-full max-w-sm"
                >
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                    <Check className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.5} />
                  </div>
                  <p className="text-gray-700 font-medium leading-snug">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-white" data-testid="about-timeline">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="label-text mb-4 block">Our Journey</span>
            <h2 className="heading-lg">Milestones</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[#0B1F3B] text-white flex items-center justify-center font-serif text-sm">
                    {milestone.year.slice(2)}
                  </div>
                  {index < milestones.length - 1 && (
                    <div className="w-px h-full bg-[#D4AF37] mt-2" />
                  )}
                </div>
                <div className="pb-8">
                  <div className="text-[#D4AF37] text-sm font-medium mb-1">{milestone.year}</div>
                  <h4 className="heading-sm mb-2">{milestone.title}</h4>
                  <p className="text-gray-600">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-[#0B1F3B]" data-testid="about-team">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">Passion of Aroma</span>
              <h2 className="heading-lg text-white mb-6">
                Where Authentic Fragrances Define Prestige
              </h2>
              <p className="text-gray-300 mb-8">
              For those seeking a trusted luxury perfume wholesaler, niche fragrance authority, or verified Middle Eastern perfume distributor, Passion of Aroma represents excellence without compromise.
              </p>
            </div>
            <div>
              <img
                src={TEAM_IMAGE}
                alt="Our Team"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
